// Error Boundary - Catches runtime errors and shows a recovery UI
// Prevents white screen of death in production

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _info: React.ErrorInfo) {
    // Errors are handled by the error boundary UI
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '24px',
          textAlign: 'center',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          color: '#1a1a1a',
          background: '#ffffff',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Noe gikk galt
          </h2>
          <p style={{ fontSize: '0.875rem', color: '#666', maxWidth: 300, margin: 0 }}>
            En uventet feil oppstod. Vennligst prøv igjen.
          </p>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '10px 24px',
                background: '#1a1a1a',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Prøv igjen
            </button>
            <button
              onClick={this.handleReload}
              style={{
                padding: '10px 24px',
                background: 'transparent',
                color: '#1a1a1a',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Last på nytt
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
